import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faChevronDown } from '@fortawesome/free-solid-svg-icons';

export interface FilterOptions {
  accounts: string[];
  regions: string[];
  resourceTypes: string[];
  costRange: { min: number; max: number };
}

export interface ResourceFilterProps {
  filters: FilterOptions;
  appliedFilters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  onApplyFilters: (filters: FilterOptions) => void;
  availableAccounts: Array<{ id: string; name: string; type: string }>;
  availableRegions: string[];
  availableResourceTypes: string[];
}

export const ResourceFilter: React.FC<ResourceFilterProps> = ({
  filters,
  appliedFilters,
  onFilterChange,
  onApplyFilters,
  availableAccounts,
  availableRegions,
  availableResourceTypes
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const handleAccountToggle = (accountId: string) => {
    const updatedAccounts = filters.accounts.includes(accountId)
      ? filters.accounts.filter(id => id !== accountId)
      : [...filters.accounts, accountId];
    
    onFilterChange({ ...filters, accounts: updatedAccounts });
  };

  const handleRegionToggle = (region: string) => {
    const updatedRegions = filters.regions.includes(region)
      ? filters.regions.filter(r => r !== region)
      : [...filters.regions, region];
    
    onFilterChange({ ...filters, regions: updatedRegions });
  };

  const handleResourceTypeToggle = (resourceType: string) => {
    const updatedTypes = filters.resourceTypes.includes(resourceType)
      ? filters.resourceTypes.filter(t => t !== resourceType)
      : [...filters.resourceTypes, resourceType];
    
    onFilterChange({ ...filters, resourceTypes: updatedTypes });
  };

  const clearAllFilters = () => {
    // "Clear all" means re-select everything (show all data)
    const allFilters = {
      accounts: availableAccounts.map(a => a.id),
      regions: [...availableRegions],
      resourceTypes: [...availableResourceTypes],
      costRange: { min: 0, max: Infinity }
    };
    onFilterChange(allFilters);
    onApplyFilters(allFilters);
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
  };

  const hasUnappliedChanges = () => {
    return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
  };

  const getActiveFilterCount = () => {
    // Count deselected items (actively filtering out)
    const deselectedAccounts = availableAccounts.length - appliedFilters.accounts.length;
    const deselectedRegions = availableRegions.length - appliedFilters.regions.length;
    const deselectedTypes = availableResourceTypes.length - appliedFilters.resourceTypes.length;
    return Math.max(0, deselectedAccounts) + Math.max(0, deselectedRegions) + Math.max(0, deselectedTypes);
  };

  const getPendingFilterCount = () => {
    return filters.accounts.length + filters.regions.length + filters.resourceTypes.length;
  };

  return (
    <div className="bg-white border border-gray-300 rounded p-3 mb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FontAwesomeIcon icon={faFilter} className="text-gray-600 text-xs" />
          <h3 className="text-sm font-bold text-gray-900">Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">
              {getActiveFilterCount()} active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {hasUnappliedChanges() && (
            <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
              {getPendingFilterCount()} pending
            </span>
          )}
          {getActiveFilterCount() > 0 && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800"
          >
            <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-3">
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Accounts Filter */}
            <div>
              <h4 className="text-xs font-bold text-gray-700 mb-2">Accounts</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {availableAccounts.map(account => (
                  <label key={account.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={filters.accounts.includes(account.id)}
                      onChange={() => handleAccountToggle(account.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-600">
                      {account.name} ({account.type.toUpperCase()})
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Regions Filter */}
            <div>
              <h4 className="text-xs font-bold text-gray-700 mb-2">Regions</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {availableRegions.map(region => (
                  <label key={region} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={filters.regions.includes(region)}
                      onChange={() => handleRegionToggle(region)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-600">{region}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Resource Types Filter */}
            <div>
              <h4 className="text-xs font-bold text-gray-700 mb-2">Resource Types</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {availableResourceTypes.map(type => (
                  <label key={type} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={filters.resourceTypes.includes(type)}
                      onChange={() => handleResourceTypeToggle(type)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-600">{type}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Apply/Reset Buttons */}
          <div className="mt-3 flex items-center justify-between pt-2 border-t border-gray-300">
            <div className="text-xs text-gray-500">
              {getPendingFilterCount() > 0 ? `${getPendingFilterCount()} filters selected` : 'No filters selected'}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onFilterChange(appliedFilters)}
                disabled={!hasUnappliedChanges()}
                className="px-2 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Reset
              </button>
              <button
                onClick={handleApplyFilters}
                disabled={!hasUnappliedChanges()}
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};